import { forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { selectAppliedInferenceSet, selectClient, selectSessionAdvisor } from '../model';
import styles from './OnboardingPdfReportV2.module.css';
import { FileText, BarChart3, TrendingUp, ShieldCheck, PieChart, Banknote } from 'lucide-react';
import {
  careEnvironmentDefs,
  carePhaseDefs,
  formatCurrency,
  formatDurationYears,
  formatInteger,
  formatPercent,
  formatThousands,
  fundingPolicyTypeDefs,
  isCarePhasePast,
  FundingPolicyType,
  isNullOrUndefined,
  isDurationCustom,
  checkPolicyCalculationTypeEquals,
  CarePhase,
} from '@shared';
import { cn } from '@/lib/utils';
import { isInferenceEdited } from '../util';

import CompanyLogoContainer from '../components/CompanyLogo';

// Helper to calculate widths for the progress bar
function calculatePhaseWidths(phases: { cost: number }[]) {
  const total = phases.reduce((acc, curr) => acc + curr.cost, 0);
  if (total === 0) return phases.map(() => 0);
  return phases.map(p => (p.cost / total) * 100);
}

function getSelfFundingName(name?: string | null) {
  const defaultName = 'Self-funding';
  return !name || name === defaultName ? defaultName : `${defaultName} (${name})`;
}

export const OnboardingPdfReportV2 = forwardRef<HTMLDivElement>((props, ref) => {
  const client = useSelector(selectClient)!;
  const sessionAdvisor = useSelector(selectSessionAdvisor);
  const { ltcLikelihoodEver, ltcAtAge } = useSelector(selectAppliedInferenceSet);

  const {
    allPhaseCosts: {
      allPhaseCareHoursProvided,
      allPhaseFamilyCareHoursProvided,
      allPhaseFamilyProvidedPercent,
      allPhaseProfessionalCareHoursProvided,
      allPhaseInflatedProfessionalShareCost,
      allPhaseCareMonthsNeeded,
    },
    fundingSources: {
      annuity: {
        hasAnnuity,
        annuityAnnualPayOut,
        annuityAnnualPayoutForLtc,
        annuityAnnualPayoutForLtcPeriodYears,
        annuityExpectedPaymentEndAge,
        annuityExpectedPaymentStartAge,
        annuityPurchasePrice,
        annuityProjectedReturnOnInvestment,
        annuityTotalValue,
        annuityProjectedCostCoverage,
        annuityNonLtcPayout,
        isDeactivated: annuityIsDeactivated,
      },
      combinedFundingTotalCost,
      combinedProjectedCostCoverage,
      combinedProjectedReturnOnInvestment,
      combinedTotalValue,
      combinedProjectedCostCoveragePercent,
      combinedNonLtcPayout,
    },
    intakeSurvey: { clientHasStartedLtc },
    multipleFundingSources,
    phaseCosts,
  } = client;

  const ltcDurationYears = allPhaseCareMonthsNeeded / 12;
  const anyCustomDurations = isDurationCustom(phaseCosts);

  // Filter Active Policies
  const ltcPolicies = Object.values(multipleFundingSources.ltcPolicy).filter(
    ltcPolicy => !ltcPolicy.isDeactivated,
  );

  // Filter Active Self Funding
  const selfFundingSources = Object.values(multipleFundingSources.selfFunding).filter(
    selfFunding => {
      if (selfFunding.isDeactivated) return false;

      const areSelfFundingFieldsPopulated =
        (selfFunding.annualRateOfReturn && selfFunding.annualRateOfReturn > 0) ||
        (selfFunding.monthlyContributionAmount && selfFunding.monthlyContributionAmount > 0) ||
        (selfFunding.existingAssetsValue && selfFunding.existingAssetsValue > 0);

      return selfFunding.hasSelfFunding || areSelfFundingFieldsPopulated;
    },
  );

  // Extract costs for the phases for the progress bar
  const phase1 = phaseCosts.find(p => p.carePhase === CarePhase.earlyCare);
  const phase2 = phaseCosts.find(p => p.carePhase === CarePhase.moderateCare);
  const phase3 = phaseCosts.find(p => p.carePhase === CarePhase.fullCare);

  const phaseOneCost = phase1?.phaseInflatedProfessionalShareCost ?? 0;
  const phaseTwoCost = phase2?.phaseInflatedProfessionalShareCost ?? 0;
  const phaseThreeCost = phase3?.phaseInflatedProfessionalShareCost ?? 0;

  const phaseWidths = calculatePhaseWidths([
    { cost: phaseOneCost },
    { cost: phaseTwoCost },
    { cost: phaseThreeCost },
  ]);

  return (
    <div
      ref={ref}
      className={styles.PdfContainer}
      id="client-pdf-report"
    >
      {/* WHITE LABEL - Company Logo/Name in Top Left */}
      <CompanyLogoContainer
        organizationDisplayName={sessionAdvisor.organizationDisplayName}
        containerClassName={styles.pdfBannerContainer}
        organizationNameClassName="text-xl font-semibold"
      />

      {/* HEADER & PREDICTIONS CARD */}
      <div className={styles.headerCard}>
        {/* HEADER */}
        <div className={styles.headerContainer}>
          <div>
            <div className={styles.reportTitle}>
              <FileText size={16} />
              Long-Term Care Financial Report
            </div>
            <h1 className={styles.clientName}>
              {client.clientFirstName} {client.clientLastName}
            </h1>
            <p className={styles.reportDescription}>
              Welcome to your long-term care plan report. This document provides a comprehensive
              overview of your current financial care plan for you and your family.
            </p>
          </div>
          <div className={styles.reportDate}>
            <div className={styles.reportDateLabel}>Report Date</div>
            <div className={styles.reportDateValue}>
              {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </div>
          </div>
        </div>

        {/* PREDICTIONS */}
        <div className={styles.predictionsGrid}>
          {!clientHasStartedLtc && (
            <div className={cn(styles.predictionCard, styles.cardPurple)}>
              <div className={styles.predictionLabel}>CARE LIKELIHOOD</div>
              <div className={styles.predictionValue}>{formatPercent(ltcLikelihoodEver, 0)}</div>
              <div className={styles.predictionSubtitle}>National Avg: 56%</div>
            </div>
          )}
          {!clientHasStartedLtc && (
            <div className={cn(styles.predictionCard, styles.cardCyan)}>
              <div className={styles.predictionLabel}>START AGE</div>
              <div className={styles.predictionValue}>{ltcAtAge} yrs</div>
              {isInferenceEdited(client, 'ltcAtAge') && (
                <div className={styles.predictionSubtitle}>(edited)</div>
              )}
            </div>
          )}
          <div className={cn(styles.predictionCard, styles.cardLightGreen)}>
            <div className={styles.predictionLabel}>
              {clientHasStartedLtc ? 'REMAINING CARE' : 'CARE DURATION'}
            </div>
            <div className={styles.predictionValue}>
              {formatDurationYears(ltcDurationYears)} yrs
            </div>
            {anyCustomDurations && <div className={styles.predictionSubtitle}>(edited)</div>}
          </div>
          <div className={cn(styles.predictionCard, styles.cardGray)}>
            <div className={styles.predictionLabel}>TOTAL HOURS</div>
            <div className={styles.predictionValue}>
              {formatThousands(allPhaseCareHoursProvided)}
            </div>
          </div>
        </div>
      </div>

      {/* PROJECTED CARE COSTS */}
      <div className={styles.sectionHeader}>
        <div className={styles.sectionIcon}>
          <BarChart3 size={20} />
        </div>
        <div className={styles.sectionTitle}>Projected Care Costs</div>
      </div>

      <div className={styles.costsContainer}>
        <div className={styles.totalCostRow}>
          <div className={styles.totalCostLabel}>
            {client.clientFirstName}'s {clientHasStartedLtc ? 'remaining' : 'projected'} costs
          </div>
          <div className={styles.totalCostValue}>
            {formatCurrency(allPhaseInflatedProfessionalShareCost)}
          </div>
        </div>

        <div className={styles.phasesGrid}>
          {phase1 && (
            <div className={cn(styles.phaseCard, styles.phaseCardHome)}>
              <div className={styles.phaseLabel}>
                {carePhaseDefs[phase1.carePhase].label.toUpperCase()}
              </div>
              <div className={cn(styles.phaseValue, styles.textGreen)}>
                {formatCurrency(phaseOneCost)}
              </div>
              <div className={styles.phaseSub}>
                {isCarePhasePast(phase1.carePhase, client)
                  ? 'Past'
                  : careEnvironmentDefs[phase1.appliedCareEnvironment].label}
              </div>
            </div>
          )}
          {phase2 && (
            <div className={cn(styles.phaseCard, styles.phaseCardMod)}>
              <div className={styles.phaseLabel}>
                {carePhaseDefs[phase2.carePhase].label.toUpperCase()}
              </div>
              <div className={cn(styles.phaseValue, styles.textTeal)}>
                {formatCurrency(phaseTwoCost)}
              </div>
              <div className={styles.phaseSub}>
                {isCarePhasePast(phase2.carePhase, client)
                  ? 'Past'
                  : careEnvironmentDefs[phase2.appliedCareEnvironment].label}
              </div>
            </div>
          )}
          {phase3 && (
            <div className={cn(styles.phaseCard, styles.phaseCardFull)}>
              <div className={styles.phaseLabel}>
                {carePhaseDefs[phase3.carePhase].label.toUpperCase()}
              </div>
              <div className={cn(styles.phaseValue, styles.textPurple)}>
                {formatCurrency(phaseThreeCost)}
              </div>
              <div className={styles.phaseSub}>
                {isCarePhasePast(phase3.carePhase, client)
                  ? 'Past'
                  : careEnvironmentDefs[phase3.appliedCareEnvironment].label}
              </div>
            </div>
          )}
        </div>

        <div className={styles.progressBarContainer}>
          <div className={styles.progressBar}>
            <div
              style={{ width: `${phaseWidths[0]}%` }}
              className={styles.progressSegmentEarly}
            />
            <div
              style={{ width: `${phaseWidths[1]}%` }}
              className={styles.progressSegmentModerate}
            />
            <div
              style={{ width: `${phaseWidths[2]}%` }}
              className={styles.progressSegmentFull}
            />
          </div>
        </div>
      </div>

      {/* Forced Page Break */}
      <div className="page-break" />

      {/* FUNDING STRATEGY */}
      <div className={styles.sectionHeader}>
        <div className={styles.sectionIcon}>
          <TrendingUp size={20} />
        </div>
        <div className={styles.sectionTitle}>Funding Strategy</div>
      </div>

      <div className={styles.fundingGrid}>
        {/* SELF FUNDING */}
        {selfFundingSources.map((source, idx) => (
          <div
            key={`self-funding-${idx}`}
            className={styles.fundingCard}
          >
            <div className={styles.fundingHeaderSection}>
              <div
                className={styles.sectionHeader}
                style={{ marginBottom: '1rem' }}
              >
                <div className={styles.sectionIcon}>
                  <TrendingUp size={16} />
                </div>
                <h3 className="text-lg font-bold">{getSelfFundingName(source.selfFundingName)}</h3>
              </div>

              <div className={styles.fundingHeaderGrid}>
                <div className={styles.fundingHeaderItem}>
                  <div className={styles.fundingHeaderLabel}>Initial Assets</div>
                  <div className={styles.fundingHeaderValue}>
                    {formatCurrency(source.existingAssetsValue ?? 0)}
                  </div>
                </div>
                <div className={styles.fundingHeaderItem}>
                  <div className={styles.fundingHeaderLabel}>Projected LTC Payout</div>
                  <div className={styles.fundingHeaderValue}>
                    {formatCurrency(source.selfFundingProjectedCostCoverage ?? 0)}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.fundingRowsContainer}>
              <FundingRow
                label="Today's Assets for LTC"
                value={
                  isNullOrUndefined(source.existingAssetsValue)
                    ? '-'
                    : formatCurrency(source.existingAssetsValue)
                }
              />
              {!clientHasStartedLtc && (
                <>
                  <FundingRow
                    label="Monthly Contribution"
                    value={
                      isNullOrUndefined(source.monthlyContributionAmount)
                        ? '-'
                        : `${formatCurrency(source.monthlyContributionAmount)} over ${source.contributionYearCount} year${source.contributionYearCount === 1 ? '' : 's'}`
                    }
                  />
                  <FundingRow
                    label="ARR | Gains Tax"
                    value={`${isNullOrUndefined(source.annualRateOfReturn) ? '-' : formatPercent(source.annualRateOfReturn, 2)} | ${isNullOrUndefined(source.gainsTaxRate) ? '-' : formatPercent(source.gainsTaxRate, 2)}`}
                  />
                </>
              )}
              <FundingRow
                label="Total Monthly Income"
                value={
                  isNullOrUndefined(source.monthlyIncomeTotal)
                    ? '-'
                    : formatCurrency(source.monthlyIncomeTotal)
                }
              />
              {!clientHasStartedLtc && (
                <>
                  <FundingRow
                    label="Total Principal"
                    value={formatCurrency(source.selfFundingTotalCost)}
                    className={styles.textPurpleMain}
                  />
                  <FundingRow
                    label="LTC Payout"
                    value={formatCurrency(source.selfFundingProjectedCostCoverage)}
                    className={styles.textBlue}
                  />
                  <FundingRow
                    label="Non-LTC Payout"
                    value={formatCurrency(source.selfFundingNonLtcPayout)}
                  />
                  <FundingRow
                    label="Total Payout"
                    value={formatCurrency(source.selfFundingTotalValue)}
                    className={styles.textBlue}
                  />
                  <FundingRow
                    label="Return on Investment"
                    value={formatPercent(source.selfFundingProjectedReturnOnInvestment, 2)}
                    className={styles.textPurpleMain}
                  />
                </>
              )}
            </div>
          </div>
        ))}

        {/* ANNUITY */}
        {hasAnnuity && !annuityIsDeactivated && (
          <div className={styles.fundingCard}>
            <div className={styles.fundingHeaderSection}>
              <div
                className={styles.sectionHeader}
                style={{ marginBottom: '1rem' }}
              >
                <div className={styles.sectionIcon}>
                  <Banknote size={16} />
                </div>
                <h3 className="text-lg font-bold">Annuity</h3>
              </div>

              <div className={styles.fundingHeaderGrid}>
                <div className={styles.fundingHeaderItem}>
                  <div className={styles.fundingHeaderLabel}>Annual Payout</div>
                  <div className={styles.fundingHeaderValue}>
                    {formatCurrency(annuityAnnualPayOut)}
                  </div>
                </div>
                <div className={styles.fundingHeaderItem}>
                  <div className={styles.fundingHeaderLabel}>Projected LTC Payout</div>
                  <div className={styles.fundingHeaderValue}>
                    {formatCurrency(annuityProjectedCostCoverage)}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.fundingRowsContainer}>
              <FundingRow
                label="LTC Annual Payout"
                value={formatCurrency(annuityAnnualPayoutForLtc)}
              />
              <FundingRow
                label="LTC Payout Period"
                value={`${formatInteger(annuityAnnualPayoutForLtcPeriodYears)} years`}
              />
              <FundingRow
                label="Expected Payout Duration"
                value={`Ages ${formatInteger(annuityExpectedPaymentStartAge)}-${formatInteger(annuityExpectedPaymentEndAge)}`}
              />
              <FundingRow
                label="Purchase Price"
                value={formatCurrency(annuityPurchasePrice)}
                className={styles.textPurpleMain}
              />
              <FundingRow
                label="LTC Coverage"
                value={formatCurrency(annuityProjectedCostCoverage)}
                className={styles.textBlue}
              />
              <FundingRow
                label="Non-LTC Payout"
                value={formatCurrency(annuityNonLtcPayout)}
              />
              <FundingRow
                label="Total Payout"
                value={formatCurrency(annuityTotalValue)}
                className={styles.textBlue}
              />
              <FundingRow
                label="Return on Investment"
                value={formatPercent(annuityProjectedReturnOnInvestment, 2)}
                className={styles.textPurpleMain}
              />
            </div>
          </div>
        )}

        {/* LTC POLICIES */}
        {ltcPolicies.map((policy, policyIdx) => (
          <div
            key={`policy-${policyIdx}`}
            className={styles.fundingCard}
          >
            <div className={styles.fundingHeaderSection}>
              <div
                className={styles.sectionHeader}
                style={{ marginBottom: '1rem' }}
              >
                <div className={styles.sectionIcon}>
                  <ShieldCheck size={16} />
                </div>
                <h3 className="text-lg font-bold">
                  {policy.policyType
                    ? fundingPolicyTypeDefs[policy.policyType].label
                    : 'LTC Insurance Policy'}
                </h3>
              </div>

              <div className={styles.fundingHeaderGrid}>
                <div className={styles.fundingHeaderItem}>
                  <div className={styles.fundingHeaderLabel}>
                    {clientHasStartedLtc
                      ? 'Remaining Benefit'
                      : checkPolicyCalculationTypeEquals(policy.policyType, [
                            FundingPolicyType.hybridLifeInsurance,
                            FundingPolicyType.lifeInsuranceWithRider,
                          ])
                        ? 'Death Benefit'
                        : 'Max Benefit'}
                  </div>
                  <div className={styles.fundingHeaderValue}>
                    {isNullOrUndefined(policy.policyMaximumBenefitAmount)
                      ? 'Unlimited'
                      : clientHasStartedLtc
                        ? formatCurrency(
                            policy.policyMaximumBenefitAmount +
                              (policy.policyContinuationBenefitAmount ?? 0) -
                              (policy.policyBenefitUtilizedToDate ?? 0),
                          )
                        : formatCurrency(policy.policyMaximumBenefitAmount)}
                  </div>
                </div>
                <div className={styles.fundingHeaderItem}>
                  <div className={styles.fundingHeaderLabel}>Projected LTC Payout</div>
                  <div className={styles.fundingHeaderValue}>
                    {formatCurrency(
                      clientHasStartedLtc
                        ? policy.policyCoverageOfRemainingLtcCosts
                        : policy.projectedCostCoverage,
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.fundingRowsContainer}>
              {checkPolicyCalculationTypeEquals(
                policy.policyType,
                FundingPolicyType.hybridLifeInsurance,
              ) && (
                <FundingRow
                  label="COB Amount"
                  value={
                    isNullOrUndefined(policy.policyContinuationBenefitAmount)
                      ? 'Unlimited'
                      : formatCurrency(policy.policyContinuationBenefitAmount)
                  }
                />
              )}

              {checkPolicyCalculationTypeEquals(policy.policyType, [
                FundingPolicyType.hybridLifeInsurance,
                FundingPolicyType.lifeInsuranceWithRider,
              ]) && (
                <FundingRow
                  label="Guaranteed Death Benefit"
                  value={formatCurrency(policy.policyMinimumGuaranteedBenefitAmount ?? 0)}
                />
              )}

              <FundingRow
                label="Policy Purchase Year"
                value={
                  isNullOrUndefined(policy.policyPremiumStartYear)
                    ? '-'
                    : formatInteger(policy.policyPremiumStartYear)
                }
              />
              <FundingRow
                label="Home/Facility Waiting Period"
                value={`${formatThousands(policy.homeCareWaitingPeriodDays ?? 0)}/${formatThousands(policy.facilityCareWaitingPeriodDays ?? 0)} days`}
              />
              <FundingRow
                label="Monthly Premium"
                value={
                  isNullOrUndefined(policy.policyPremiumMonthlyCost)
                    ? '-'
                    : formatCurrency(policy.policyPremiumMonthlyCost)
                }
              />
              {policy.policyPremiumIncreaseRate && (
                <FundingRow
                  label="Premium Increase Rate"
                  value={formatPercent(policy.policyPremiumIncreaseRate)}
                />
              )}
              {policy.policyLimitedPayYears && (
                <FundingRow
                  label="Limited Pay Period"
                  value={`${formatThousands(policy.policyLimitedPayYears)} years`}
                />
              )}
              <FundingRow
                label="Lump-Sum Payment"
                value={
                  isNullOrUndefined(policy.policyLumpSumPayment)
                    ? '-'
                    : formatThousands(policy.policyLumpSumPayment)
                }
              />
              <FundingRow
                label="Home Care Max*"
                value={
                  isNullOrUndefined(policy.dailyHomeBenefitAmount)
                    ? '-'
                    : `${formatCurrency(policy.dailyHomeBenefitAmount)}/day`
                }
              />
              <FundingRow
                label="Independent Living Max*"
                value={
                  isNullOrUndefined(policy.dailyIndependentLivingBenefitAmount)
                    ? '-'
                    : `${formatCurrency(policy.dailyIndependentLivingBenefitAmount)}/day`
                }
              />
              <FundingRow
                label="Assisted Living Max*"
                value={
                  isNullOrUndefined(policy.dailyAssistedLivingBenefitAmount)
                    ? '-'
                    : `${formatCurrency(policy.dailyAssistedLivingBenefitAmount)}/day`
                }
              />
              <FundingRow
                label="Nursing Home Max*"
                value={
                  isNullOrUndefined(policy.dailyNursingHomeBenefitAmount)
                    ? '-'
                    : `${formatCurrency(policy.dailyNursingHomeBenefitAmount)}/day`
                }
              />
              <div className={styles.fundingFootnote}>
                * Daily maximum estimated by converting a monthly maximum to a daily value -
                dividing by 30. Some policies use a single maximum across care settings.
              </div>
              <FundingRow
                label="Inflation Protection"
                value={
                  isNullOrUndefined(policy.policyInflationProtection)
                    ? '-'
                    : `${formatPercent(policy.policyInflationProtection, 2)} (${policy.isSimpleInflationProtection ? 'Simple' : 'Compound'})`
                }
              />
              <FundingRow
                label="Payment Type"
                value={
                  isNullOrUndefined(policy.isIndemnityPolicyPayment) ||
                  policy.isIndemnityPolicyPayment === false
                    ? 'Reimbursement'
                    : 'Indemnity'
                }
              />
              <FundingRow
                label="Premiums Paid"
                value={formatCurrency(policy.policyTotalCost)}
                className={styles.textPurpleMain}
              />
              <FundingRow
                label="LTC Coverage"
                value={formatCurrency(
                  clientHasStartedLtc
                    ? policy.policyCoverageOfRemainingLtcCosts
                    : policy.projectedCostCoverage,
                )}
                className={styles.textBlue}
              />
              <FundingRow
                label="Non-LTC Payout"
                value={formatCurrency(policy.policyNonLtcPayout)}
              />
              <FundingRow
                label="Total Payout"
                value={formatCurrency(policy.policyTotalValue)}
                className={styles.textBlue}
              />
              <FundingRow
                label="Return on Investment"
                value={formatPercent(policy.policyProjectedReturnOnInvestment, 2)}
                className={styles.textPurpleMain}
              />
            </div>
          </div>
        ))}
      </div>

      {/* COMBINED SUMMARY */}
      <div className={styles.sectionHeader}>
        <div className={styles.sectionIcon}>
          <PieChart size={20} />
        </div>
        <div className={styles.sectionTitle}>Combined Funding Summary</div>
      </div>

      <div className={styles.summaryContainer}>
        <div className={styles.summaryGrid}>
          <div className={styles.summaryItem}>
            <div className={styles.summaryLabel}>TOTAL INVESTED</div>
            <div className={styles.summaryValue}>
              {formatCurrency(combinedFundingTotalCost ?? 0)}
            </div>
          </div>
          <div className={styles.summaryItem}>
            <div className={styles.summaryLabel}>LTC COVERAGE</div>
            <div className={styles.summaryValue}>
              {formatCurrency(combinedProjectedCostCoverage ?? 0)}
            </div>
          </div>
          <div className={styles.summaryItem}>
            <div className={styles.summaryLabel}>NON-LTC PAYOUT</div>
            <div className={styles.summaryValue}>{formatCurrency(combinedNonLtcPayout ?? 0)}</div>
          </div>
          <div className={styles.summaryItem}>
            <div className={styles.summaryLabel}>COVERAGE %</div>
            <div className={styles.summaryValue}>
              {formatPercent(combinedProjectedCostCoveragePercent ?? 0, 2)}
            </div>
          </div>
          <div className={styles.summaryItem}>
            <div className={styles.roiBadge}>
              <div className="mb-1 text-[10px] font-bold uppercase opacity-80">COMBINED ROI</div>
              <div className="text-xl font-bold">
                {formatPercent(combinedProjectedReturnOnInvestment ?? 0, 2)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CARE HOURS DISTRIBUTION */}
      <div className={styles.careHoursContainer}>
        <h3 className={styles.careHoursTitle}>
          {clientHasStartedLtc ? 'Remaining Care Hours' : 'Total Care Hours'} Distribution
        </h3>
        <div className={styles.hoursDistribution}>
          <div>
            <div className={styles.hoursRow}>
              <div className={styles.hoursLabel}>Professional Care Services</div>
              <div className={styles.hoursValue}>
                {formatThousands(allPhaseProfessionalCareHoursProvided)} hrs
              </div>
            </div>
            <div className={styles.hoursBarBg}>
              <div
                className={cn(styles.hoursBarFill, styles.barPurple)}
                style={{
                  width: `${(allPhaseProfessionalCareHoursProvided / allPhaseCareHoursProvided) * 100}%`,
                }}
              />
            </div>
          </div>
          <div>
            <div className={styles.hoursRow}>
              <div className={styles.hoursLabel}>Family Provided Care</div>
              <div className={styles.hoursValue}>
                {formatThousands(allPhaseFamilyCareHoursProvided)} hrs
              </div>
            </div>
            <div className={styles.hoursBarBg}>
              <div
                className={cn(styles.hoursBarFill, styles.barTeal)}
                style={{
                  width: `${(allPhaseFamilyCareHoursProvided / allPhaseCareHoursProvided) * 100}%`,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

OnboardingPdfReportV2.displayName = 'OnboardingPdfReportV2';

function FundingRow({
  label,
  value,
  className,
}: {
  label: string;
  value: string | number;
  className?: string;
}) {
  return (
    <div className={styles.fundingRow}>
      <span className={styles.fundingRowLabel}>{label}</span>
      <span className={cn(styles.fundingRowValue, className)}>{value}</span>
    </div>
  );
}
